import { useCallback, useEffect, useState } from 'react';
import { eventsApi } from '../api/events';
import { ALL_PERMISSIONS_FALSE, ALL_PERMISSIONS_TRUE } from '../api/eventTeam';
import type { Event, EventManagerPermissions } from '../types';

interface UseEventPermissionsResult {
  event: Event | null;
  isCreator: boolean;
  permissions: EventManagerPermissions;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Returns the current user's role and permissions for an event.
 *
 * The backend bakes `userRole` and `userPermissions` into `GET /events/:id`,
 * so this hook is just a typed wrapper. While loading or on error, it
 * defaults to all-permissions-false — so gated UI fails closed.
 *
 * Pass an already-fetched event via `seed` to avoid a duplicate request.
 */
export function useEventPermissions(
  eventId: string | null | undefined,
  seed?: Event | null,
): UseEventPermissionsResult {
  const [event, setEvent] = useState<Event | null>(seed ?? null);
  const [isLoading, setIsLoading] = useState(!seed && Boolean(eventId));
  const [error, setError] = useState<string | null>(null);

  const fetchEvent = useCallback(async () => {
    if (!eventId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await eventsApi.get(eventId);
      setEvent(data);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setError(e.response?.data?.error || e.message || 'Failed to load permissions');
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (seed) {
      setEvent(seed);
      setIsLoading(false);
      return;
    }
    if (eventId) {
      void fetchEvent();
    }
  }, [eventId, seed, fetchEvent]);

  const isCreator = event?.userRole === 'creator';
  const permissions: EventManagerPermissions = !event
    ? ALL_PERMISSIONS_FALSE
    : isCreator
      ? ALL_PERMISSIONS_TRUE
      : event.userPermissions ?? ALL_PERMISSIONS_FALSE;

  return {
    event,
    isCreator,
    permissions,
    isLoading,
    error,
    refresh: fetchEvent,
  };
}
